<template>
  <canvas
    ref="canvas"
    :width="$refs.video?.videoWidth"
    height="480"
    style="display: none"
  ></canvas>
  <!-- Main Content Area -->
  <div v-if="!loading" class="annotation-container">
    <!-- Top Control Bar -->
    <div class="control-bar">
      <div class="control-bar-left">
        <a-button type="text" class="control-btn" @click="openHelpModal">
          <book-outlined />
          Help & Shortcuts
        </a-button>
        <a-divider type="vertical" />
        <a-button
          v-if="!loading && !isVisualize"
          type="primary"
          size="small"
          @click="handleAddSaveFrame"
        >
          <camera-outlined />
          Save Frame (F)
        </a-button>

        <a-button
          v-if="!loading && !isVisualize"
          type="primary"
          size="small"
          @click="handleSaveFrameAsCameraReference"
        >
          <SaveOutlined />
          Set Frame as Reference
        </a-button>
      </div>

      <div class="control-bar-right">
        <a-button type="text" class="control-btn" @click="showSettings = true">
          <setting-outlined />
          Settings
        </a-button>

        <CloseCircleFilled
          style="cursor: pointer"
          @click="$emit('closeModal')"
        />
      </div>
    </div>

    <!-- Main Content Row -->
    <div class="main-content-row">
      <!-- Left Panel - Saved Frames -->
      <div class="left-panel">
        <a-card
          class="saved-frames-card"
          size="small"
          :title="`Saved Frames (${saveFrames.length})`"
        >
          <div v-if="!saveFrames.length" class="empty-frames">
            <a-empty description="No saved frames yet" :image="false">
              <template #description>
                <span class="empty-text">No saved frames yet</span>
                <br />
                <small>Press F or click Save Frame button</small>
              </template>
            </a-empty>
          </div>
          <div v-else class="saved-frames-container">
            <a-list
              size="small"
              :data-source="saveFrames"
              class="saved-frames-list-main"
            >
              <template #renderItem="{ item, index }">
                <a-list-item class="saved-frame-item-main">
                  <span class="frame-text">Frame: {{ item }}</span>
                  <template #actions>
                    <a-button
                      type="text"
                      danger
                      size="small"
                      @click="removeFrame(item)"
                      title="Remove frame"
                    >
                      <delete-outlined />
                    </a-button>
                  </template>
                </a-list-item>
              </template>
            </a-list>
          </div>
        </a-card>
      </div>

      <!-- Middle Panel - Video Info -->
      <div class="middle-panel">
        <a-card class="info-card" size="small" title="Video Information">
          <video-info
            :videoInfo="video"
            :videoMetaData="videoMetaData"
            :totalFrames="totalFrames"
            :isVisualize="isVisualize"
          />
        </a-card>
      </div>

      <!-- Right Section - Video Area -->
      <div class="video-section">
        <VideoPlayer
          :style="{ width: '10px' }"
          v-show="false"
          ref="videoPlayer"
        />
        <div class="video-container">
          <video
            v-if="video.availableLocally"
            id="annotate-video-local"
            ref="video"
            muted
            class="main-video"
            :src="video?.localURL || video?.fileURL"
            crossorigin="anonymous"
          ></video>
          <video
            v-else
            id="annotate-video-local"
            ref="video"
            muted
            class="main-video"
            :src="video?.localURL || video?.fileURL"
            crossorigin="anonymous"
          ></video>
        </div>
      </div>
    </div>

    <!-- Labelling Tool Section -->
    <div class="labelling-section">
      <labelling-tool
        ref="labellingComponent"
        :stepsList="stepsList"
        :prevMetaData="prevMetaData"
        :stepsToVisualize="stepsToVisualize"
        :videoInfo="video"
        :videoMetaData="videoMetaData"
        :taskId="taskId"
        :taskName="taskName"
        :totalFrames="totalFrames"
        :frame="currentFrame"
        :is-visualize="isVisualize"
        :zoom-in="zoomIn"
        :organization="organization"
        :update-task-record="updateTaskRecord"
        :steps-translation-mapping="stepsTranslationMapping"
        :steps-to-index-mapping="stepsToIndexMapping"
        :barcoded-steps="barcodedSteps"
        :should-display-tooltip="!deviceSelectionModalVisible"
        @frameChange="handleFrameChange"
        @onSave="(data) => $emit('onSave', data)"
      ></labelling-tool>
    </div>
  </div>
  <a-modal
    v-model:visible="deviceSelectionModalVisible"
    :body-style="{ height: '50vh', overflowY: 'auto' }"
    title="Select Device"
    @ok="handleDeviceSelectionConfirm"
    okText="Confirm"
    cancelText="Cancel"
    :okButtonProps="{ disabled: !deviceId }"
  >
    <a-input
      v-model:value="searchDevice"
      placeholder="Search device by name or mac address"
      class="w-100 mb-2"
    >
      <template #suffix>
        <search-outlined style="color: rgba(0, 0, 0, 0.45)" />
      </template>
    </a-input>
    <a-table
      :columns="DeviceColumnNames"
      :dataSource="devicesToDisplay"
      :rowSelection="{
        type: 'radio',
        selectedRowKeys: deviceId ? [deviceId] : [],
        onChange: onDeviceSelect,
      }"
      rowKey="Device"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.dataIndex === 'serialNumber'">
          {{ record.Serial_number }}
        </template>
        <template v-else-if="column.dataIndex === 'displayName'">
          {{ record.display_name || record.name || 'N/A' }}
        </template>
      </template>
    </a-table>
  </a-modal>

  <!-- Help Modal with Instructions and Shortcuts -->
  <a-modal
    v-model:visible="showHelpModal"
    title="Help & Shortcuts"
    :footer="null"
    width="40vw"
    class="help-modal"
    :z-index="2000"
    @ok="showHelpModal = false"
    @cancel="showHelpModal = false"
  >
    <a-tabs default-active-key="instructions" class="help-tabs">
      <a-tab-pane key="instructions" tab="Instructions">
        <div style="padding: 20px">
          <h4>📋 Labelling Instructions</h4>
          <labelling-instruction></labelling-instruction>
          <div v-if="!stepsList || !stepsList.length">
            <a-alert
              message="Instructions Loading"
              description="Labelling instructions will appear here once the task data is loaded."
              type="info"
              show-icon
            />
          </div>
        </div>
      </a-tab-pane>
      <a-tab-pane key="shortcuts" tab="Keyboard Shortcuts">
        <div style="padding: 20px">
          <keyboard-keys-info
            :is-visualize="isVisualize"
            :zoom-in="zoomIn"
            :total-frames="totalFrames"
            @changeZoom="(val) => (zoomIn = val)"
          />
          <div style="margin-top: 20px">
            <a-alert
              message="Quick Reference"
              description="Press F to save current frame • Press Escape to close modals • Use arrow keys for navigation"
              type="success"
              show-icon
            />
          </div>
        </div>
      </a-tab-pane>
    </a-tabs>
  </a-modal>

  <!-- Settings Modal -->
  <a-modal
    v-model:visible="showSettings"
    title="Settings"
    :footer="null"
    width="40vw"
    class="settings-modal"
    :z-index="2000"
    @ok="showSettings = false"
    @cancel="showSettings = false"
  >
    <a-space direction="vertical" size="large" style="width: 100%">
      <div>
        <a-typography-title :level="5">Display Settings</a-typography-title>
        <a-switch
          v-model:checked="zoomIn"
          checked-children="Zoom In"
          un-checked-children="Normal"
          @change="(val) => (zoomIn = val)"
        />
      </div>

      <div>
        <a-typography-title :level="5">Video Settings</a-typography-title>
        <a-space direction="vertical" style="width: 100%">
          <div class="setting-item">
            <span>Total Frames: {{ totalFrames }}</span>
          </div>
          <div class="setting-item" v-if="videoMetaData">
            <span>FPS: {{ videoMetaData.fps }}</span>
          </div>
          <div class="setting-item" v-if="videoMetaData">
            <span>Duration: {{ videoMetaData.duration }}</span>
          </div>
        </a-space>
      </div>
    </a-space>
  </a-modal>
</template>

<script>
import { getStepToIndexMapping, getTaskNameAndProcess } from '@/utils/task.js';
import {
  BookOutlined,
  CameraOutlined,
  CloseCircleFilled,
  DeleteOutlined,
  SettingOutlined,
  SaveOutlined,
} from '@ant-design/icons-vue';
import httpClient from 'src/service/httpClient';
import TaskRecordService from 'src/services/taskRecord';
import { mapActions, mapGetters } from 'vuex';
import getNumberOfFrames from '../../../shared/Helpers/getNumberOfFrames';
import getStepsList from '../../../shared/Helpers/getStepsList';
import getTranslatedSteps from '../../../shared/Helpers/getTranslatedSteps.js';
import LabellingInstruction from './Instrunction.vue';
import KeyboardKeysInfo from './KeyboardKeysInfo.vue';
import LabellingTool from './Labelling.vue';
import VideoPlayer from './Video.vue';
import VideoInfo from './VideoInfo.vue';
import { DeviceColumnNames } from './config';

export default {
  components: {
    VideoInfo,
    VideoPlayer,
    LabellingTool,
    LabellingInstruction,
    KeyboardKeysInfo,
    BookOutlined,
    CameraOutlined,
    SettingOutlined,
    DeleteOutlined,
    CloseCircleFilled,
    SaveOutlined,
  },
  inject: ['toast'],
  props: {
    video: {},
    taskId: {},
    isVisualize: { default: false },
    prevMetaData: {},
    loading: {},
    organization: {},
    updateTaskRecord: Function,
    isValidation: { default: false },
  },
  emits: ['onSave', 'stopLoading', 'updateLoading', 'closeModal'],
  data: function () {
    return {
      taskName: '',
      stepsList: [],
      stepsToVisualize: {},
      currentFrame: 0,
      totalFrames: Number.MAX_SAFE_INTEGER,
      videoMetaData: null,
      zoomIn: false,
      peerConnection: null,
      connection: null,
      value: 0,
      stunCheckbox: true,
      offer: null,
      config: [{ sdpSemantics: 'unified-plan' }],
      rtcInteval: null,
      saveFrames: [],
      stepsTranslationMapping: {},
      predsToStepsMap: {},
      stepsToIndexMapping: {},
      barcodedSteps: [],
      deviceId: null,
      deviceSelectionModalVisible: false,
      deviceSearchText: '',
      filteredDevices: [],
      searchDevice: '',
      showHelpModal: false,
      showSettings: false,
    };
  },
  computed: {
    ...mapGetters([
      'organization',
      'role',
      'webSocketConnection',
      'taskDetails',
      'taskStepsCount',
      'translationDetails',
      'translationLanguage',
      'selectedLang',
      'associateBarcodes',
      'allDevices',
      'selectedTask',
    ]),

    annotateVideoUrl() {
      let videoURL = this.video?.fileURL || this.video?.localURL;
      if (this.organization === 'celestica') {
        videoURL = videoURL.replace('.us-east-2.', '.ap-southeast-1.');
      }
      return videoURL;
    },
    devicesToDisplay() {
      const searchTerm = this.searchDevice.toLowerCase();
      if (!searchTerm) {
        return [...this.allDevices];
      }

      return this.allDevices.filter((device) => {
        return (
          device.Serial_number?.toLowerCase().includes(searchTerm) ||
          device.display_name?.toLowerCase().includes(searchTerm) ||
          device.name?.toLowerCase().includes(searchTerm)
        );
      });
    },
  },

  watch: {
    selectedLang(value) {
      this.setStepTranslations(value);
    },
  },

  created() {
    window.onbeforeunload = () => '';
  },

  setup() {
    return { DeviceColumnNames };
  },

  async mounted() {
    if (!this.taskDetails) return;
    this.setTaskDetails(this.taskDetails, true);
    this.setStepTranslations(this.selectedLang);
    this.getBarcodedSteps();
    // if (roles.labeler === this.role) this.setupWebRTC();
    document.addEventListener('keyup', this.handleKeyUpEvents);
    document.addEventListener('keydown', this.handleKeyDownEvents);
    window.addEventListener('beforeunload', this.handleSaveCaptureFrame);
    window.addEventListener('unload', this.handleSaveCaptureFrame);
    if (!this.allDevices || this.allDevices.length == 0) {
      await this.getOrgDevices(this.organization);
    }
  },

  beforeUnmount() {
    this.$emit('updateLoading', true);
    document.removeEventListener('keyup', this.handleKeyUpEvents);
    document.removeEventListener('keydown', this.handleKeyDownEvents);
    window.removeEventListener('beforeunload', this.handleSaveCaptureFrame);
    window.removeEventListener('unload', this.handleSaveCaptureFrame);
    window.onbeforeunload = null;
    this.handleSaveCaptureFrame();
  },

  methods: {
    ...mapActions(['setSelectedLanguage', 'getOrgDevices']),

    openHelpModal() {
      console.log('Opening help modal...', this.showHelpModal);
      this.showHelpModal = true;
      console.log('Help modal state after:', this.showHelpModal);
    },

    handleKeyDownEvents(event) {
      if (event.key?.toLowerCase() === 'f') this.saveCurrentFrame();
    },

    handleKeyUpEvents(event) {
      if (event.key === 'Escape') {
        this.$emit('closeModal');
      }
    },

    setStepTranslations(lang) {
      if (lang === this.translationLanguage) {
        this.stepsTranslationMapping = getTranslatedSteps(
          this.taskDetails,
          this.translationDetails
        );
      } else {
        this.stepsTranslationMapping = getTranslatedSteps(this.taskDetails);
      }
    },

    setTaskDetails(taskDetail, getData) {
      const temp = getStepsList(taskDetail);
      this.taskName = temp?.name || '';
      this.stepsList = temp?.steps || [];
      const { processes } = getTaskNameAndProcess(taskDetail);
      const stepToIndexMap = getStepToIndexMapping(processes);
      this.stepsToIndexMapping = stepToIndexMap;
      getData && this.getVideoMetaData();
    },

    getBarcodedSteps() {
      this.barcodedSteps = this.associateBarcodes
        ?.map((barcode) => barcode.step_index)
        .sort();
    },

    handleAddSaveFrame() {
      const { currentFrame } = this;
      if (!this.saveFrames.includes(currentFrame))
        this.saveFrames = [...this.saveFrames, currentFrame];
    },

    async handleSaveFrameAsCameraReference() {
      this.deviceSelectionModalVisible = true;
    },

    onDeviceSelect(selectedRowKeys) {
      this.deviceId = selectedRowKeys[0] || null;
    },

    async handleDeviceSelectionConfirm() {
      if (!this.deviceId) {
        this.$message.error('Please select a device');
        return;
      }

      try {
        const referencePathInS3 = `${this.taskName}/CameraReference/${this.deviceId}`;
        const ctx = this.$refs.canvas.getContext('2d');
        let imageName = 'reference_frame_original.jpg';
        ctx.drawImage(this.$refs.video, 0, 0, this.$refs.video.videoWidth, 480);
        this.$refs.canvas.toBlob(
          async (image) =>
            await this.$refs.videoPlayer.uploadCameraReferenceImageOnCloud(
              image,
              imageName,
              referencePathInS3,
              this.selectedTask,
              this.deviceId
            ),
          'image/jpeg'
        );

        this.$toast.success('Camera reference image saved successfully!');
        this.deviceSelectionModalVisible = false;
      } catch (error) {
        this.$toast.error(`Failed to save reference image: ${error.message}`);
        console.error(error);
      }
    },

    saveCurrentFrame() {
      if (this.loading || this.isVisualize) return;
      this.handleAddSaveFrame();
    },

    async handleSaveCaptureFrame() {
      if (!this.saveFrames.length) return;
      const payload = {
        task_id: this.taskId,
        frames: this.saveFrames,
        file_name: this.video.fileName,
        file_url: this.video.fileURL,
      };
      await httpClient.post(
        'generic/task/save_annotation_images/',
        payload,
        false,
        false,
        false,
        '',
        ''
      );
      this.saveFrames = [];
    },

    removeFrame(frame) {
      this.saveFrames = this.saveFrames.filter((f) => f !== frame);
    },

    uploadCurrentFrameToS3() {
      const { fileName } = this.video;
      this.$refs.videoPlayer.uploadFrameForAnnotationtest1(
        this.taskName,
        fileName,
        this.currentFrame
      );
    },

    handleFrameChange(frame) {
      this.currentFrame = frame;
      const videoFPS = this.videoMetaData?.fps || this.video?.fps;
      const frameToMove = (Number(frame) / videoFPS).toFixed(25);
      this.$refs.video.currentTime = frameToMove;
    },

    getPreds(pred) {
      const key = JSON.stringify(pred);
      if (this.predsToStepsMap[key]) return this.predsToStepsMap[key];

      const result = [];

      pred = pred.reduce((res, el) => {
        res[el] = el;
        return res;
      }, {});

      for (let i = 0; i < this.taskStepsCount; i++) result.push(pred[i]);

      this.predsToStepsMap = {
        ...this.predsToStepsMap,
        key: result,
      };
      return result;
    },

    async parseTaskDetails() {
      const stepsDict = {};
      const entries = Object.entries(this.video?.predictions).map(
        ([frame, value]) => {
          const { pred } = value;
          const res = this.getPreds(pred);
          return [frame, { ...value, pred: res }];
        }
      );

      if (entries.length === 0) return;

      let prevValue = entries[0][1]?.pred || entries[0][1],
        prevKey = entries[0][0];

      entries.forEach(([key, value]) => {
        const first = value.pred ? value.pred : value;

        prevValue.forEach((prev, index) => {
          if (!stepsDict[first[index]]) {
            stepsDict[first[index]] = [{ start: key }];
            if (prev !== first[index]) {
              const lastIndex = stepsDict[prev].length - 1;
              const prevEl = stepsDict[prev][lastIndex];
              stepsDict[prev][lastIndex] = { ...prevEl, end: prevKey };
            }
          } else if (prev !== first[index]) {
            stepsDict[first[index]].push({ start: key });
            const lastIndex = stepsDict[prev].length - 1;
            const prevEl = stepsDict[prev][lastIndex];
            stepsDict[prev][lastIndex] = { ...prevEl, end: prevKey };
          }
        });

        prevValue = value?.pred || value;
        prevKey = key;
      });
      const prev = prevValue[0];

      const lastIndex = stepsDict[prev].length - 1;
      const prevEl = stepsDict[prev][lastIndex];
      stepsDict[prev][lastIndex] = { ...prevEl, end: prevKey };

      this.stepsToVisualize = stepsDict;
    },

    async getVideoMetaData() {
      if (!this.video) return;
      const payload = { id: this.video?.id };
      if (this.isValidation) payload['is_validation'] = true;
      const [error, res] = await TaskRecordService.getVideoMetaData(
        payload,
        false
      );
      if (error) {
        console.log({ error });
        return 30;
      }
      const { data } = res;
      if (!data) {
        this.setDefaultVideoMetaData();
        return;
      }

      this.setVideoMetaData(data);
      if (this.isVisualize) this.parseTaskDetails();
      this.$emit('stopLoading');
    },

    setVideoMetaData(data) {
      let { duration, fps, total_frames } = data;

      this.totalFrames = total_frames;

      this.videoMetaData = {
        fps,
        total_frames,
        duration,
      };
    },

    setDefaultVideoMetaData() {
      if (!this.video) return;
      const { fps, duration } = this.video;
      this.totalFrames = getNumberOfFrames(duration, fps);
    },
  },
};
</script>
<style scoped>
.annotation-container {
  height: 90vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.control-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: 10;
}

.control-bar-left,
.control-bar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-btn {
  border-radius: 6px;
  transition: all 0.2s ease;
  font-weight: 500;
}

.control-btn:hover {
  background: #f0f0f0;
  transform: translateY(-1px);
}

.main-content-row {
  flex: 1;
  display: flex;
  gap: 16px;
  padding: 16px;
  overflow: hidden;
}

.left-panel {
  width: 280px;
  flex-shrink: 0;
}

.middle-panel {
  width: 280px;
  flex-shrink: 0;
}

.info-card {
  height: 100%;
}

.saved-frames-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.saved-frames-card :deep(.ant-card-body) {
  flex: 1;
  padding: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.empty-frames {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.empty-text {
  color: #8c8c8c;
  font-size: 14px;
}

.saved-frames-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.saved-frames-list-main {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.saved-frame-item-main {
  transition: all 0.2s ease;
  padding: 8px 12px;
}

.saved-frame-item-main:hover {
  background: #f8f9fa;
}

.frame-text {
  font-weight: 500;
  color: #1890ff;
}

.video-section {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #ffffff;
  border-radius: 2px;
  position: relative;
}

.video-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.main-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.labelling-section {
  height: 35vh;
  background: white;
  border-top: 1px solid #e8e8e8;
  overflow: hidden;
}

/* Drawer Styles */
.info-drawer :deep(.ant-drawer-body),
.instructions-drawer :deep(.ant-drawer-body),
.saved-frames-drawer :deep(.ant-drawer-body) {
  padding: 16px;
}

.saved-frames-list {
  margin-top: 16px;
}

.saved-frame-item {
  transition: all 0.2s ease;
  border-radius: 6px;
  margin-bottom: 8px;
}

.saved-frame-item:hover {
  background: #f8f9fa;
  transform: translateX(4px);
}

.frame-number {
  font-weight: 600;
  color: #1890ff;
}

/* Modal Styles */
.help-modal :deep(.ant-modal-body),
.settings-modal :deep(.ant-modal-body) {
  padding: 24px;
}

.help-modal :deep(.ant-modal-wrap),
.settings-modal :deep(.ant-modal-wrap) {
  z-index: 2000 !important;
}

.help-modal :deep(.ant-modal),
.settings-modal :deep(.ant-modal) {
  z-index: 2001 !important;
  position: relative !important;
}

.help-modal :deep(.ant-modal-mask),
.settings-modal :deep(.ant-modal-mask) {
  z-index: 1999 !important;
}

.help-tabs :deep(.ant-tabs-content-holder) {
  max-height: 60vh;
  overflow-y: auto;
}

.help-tabs :deep(.ant-tabs-tab) {
  font-weight: 500;
}

/* Override any tooltip z-index when modal is open */
.help-modal :deep(.ant-tooltip),
.settings-modal :deep(.ant-tooltip) {
  z-index: 1998 !important;
}

/* Global tooltip z-index override when modals are present */
:deep(.ant-tooltip) {
  z-index: 1000;
}

/* Ensure slider tooltips don't appear above modals */
:deep(.ant-slider-tooltip) {
  z-index: 1000 !important;
}

.setting-item {
  padding: 8px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
}

.setting-item:last-child {
  border-bottom: none;
}

/* Responsive Design */
@media only screen and (max-width: 768px) {
  .control-bar {
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .control-bar-left,
  .control-bar-right {
    flex-wrap: wrap;
    justify-content: center;
  }

  .main-content-row {
    flex-direction: column;
    padding: 10px;
    gap: 10px;
  }

  .left-panel {
    width: 100%;
    order: 1;
  }

  .middle-panel {
    width: 100%;
    order: 2;
  }

  .video-section {
    height: 40vh;
    order: 3;
  }

  .labelling-section {
    height: 40vh;
  }
}

@media only screen and (max-width: 480px) {
  .control-btn {
    font-size: 12px;
    padding: 4px 8px;
  }

  .labelling-section {
    height: 45vh;
  }
}

/* Animation */
.control-btn,
.saved-frame-item {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Focus states for accessibility */
.control-btn:focus {
  outline: 2px solid #1890ff;
  outline-offset: 2px;
}
</style>
